<template>
  <div>
    <AccFoodGraphics
      v-for="(foodData, foodType) in foodProfilesByHour"
      :key="foodType"
      :food-type="foodType"
      :food-data="foodData"
      :foods-by-company="foodsByCompany"
      :is-loading="isLoading"
    />
  </div>
</template>

<script>
//import ChartComponent from './ChartComponent.vue'
import AccFoodGraphics from './AccFoodGraphics.vue'

export default {
  name: 'AccFood',
  components: {
    //ChartComponent,
    AccFoodGraphics,
  },
  props: {
    siloProfiles: {
      type: Object,
      required: true
    },
    foodsByCompany: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    foodProfilesByDay() {
      // Create a map to store profiles grouped by food type and then by day
      const profilesByFood = new Map()

      // Process each array of profiles
      this.siloProfiles.forEach(profileArray => {
        // Check if profileArray is an array
        if (!Array.isArray(profileArray)) {
          return
        }

        // Process each profile in the array
        profileArray.forEach(profile => {
          try {
            // Get the food type and date
            const foodType = profile.food
            
            // Skip profiles with null food type
            if (foodType === null || foodType === undefined) {
              return
            }

            const dateObj = new Date(profile.datetime_fetched)
            
            // Check if date is valid
            if (isNaN(dateObj.getTime())) {
              return // Skip this profile
            }

            // Extraer fecha local (YYYY-MM-DD)
            const year = dateObj.getFullYear()
            const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
            const day = dateObj.getDate().toString().padStart(2, '0')
            const date = `${year}-${month}-${day}`
            
            // Initialize food type map if it doesn't exist
            if (!profilesByFood.has(foodType)) {
              profilesByFood.set(foodType, new Map())
            }

            const foodMap = profilesByFood.get(foodType)
            
            if (!foodMap.has(date)) {
              foodMap.set(date, {
                totalLevel: 0,
                count: 0,
                profiles: []
              })
            }

            const dayData = foodMap.get(date)
            dayData.totalLevel += profile.weight
            dayData.count++
            dayData.profiles.push(profile)
          } catch (error) {
            console.warn('Error processing profile:', error)
          }
        })
      })

      // Convert the nested maps to the final structure
      const result = {}
      profilesByFood.forEach((foodMap, foodType) => {
        result[foodType] = Array.from(foodMap.entries()).map(([date, data]) => ({
          date,
          averageLevel: data.totalLevel / data.count,
          totalLevel: data.totalLevel,
          count: data.count,
          profiles: data.profiles
        })).sort((a, b) => new Date(a.date) - new Date(b.date))
      })
      return result
    },
    foodProfilesByHour() {
      // Create a map to store profiles grouped by food type and then by hour
      const profilesByFood = new Map()
      // Create a map to store all known values for each silo
      const siloValues = new Map()

      // Process each array of profiles
      this.siloProfiles.forEach(profileArray => {
        // Check if profileArray is an array
        if (!Array.isArray(profileArray)) {
          return
        }     

        // First pass: collect all values for each silo
        profileArray.forEach(profile => {
          try {
            if (profile.food === null || profile.food === undefined) return
            
            const dateObj = new Date(profile.datetime_fetched)
            if (isNaN(dateObj.getTime())) return

            const year = dateObj.getFullYear()
            const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
            const day = dateObj.getDate().toString().padStart(2, '0')
            const hour = dateObj.getHours().toString().padStart(2, '0')
            const dateHour = `${year}-${month}-${day} ${hour}:00`

            // Initialize array for this silo if it doesn't exist
            if (!siloValues.has(profile.silo)) {
              siloValues.set(profile.silo, [])
            }
            
            // Store the value with its timestamp
            siloValues.get(profile.silo).push({
              value: profile.weight,
              timestamp: dateHour
            })
          } catch (error) {
            console.warn('Error processing profile in first pass:', error)
          }
        })

        // Sort values by timestamp for each silo
        siloValues.forEach(values => {
          values.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
        })

        // Process each profile in the array
        profileArray.forEach(profile => {
          try {
            // Get the food type and date
            const foodType = profile.food
            
            // Skip profiles with null food type
            if (foodType === null || foodType === undefined) {
              return
            }

            const dateObj = new Date(profile.datetime_fetched)
            
            // Check if date is valid
            if (isNaN(dateObj.getTime())) {
              return // Skip this profile
            }

            // Extraer fecha y hora local (YYYY-MM-DD HH:00)
            const year = dateObj.getFullYear()
            const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
            const day = dateObj.getDate().toString().padStart(2, '0')
            const hour = dateObj.getHours().toString().padStart(2, '0')
            const dateHour = `${year}-${month}-${day} ${hour}:00`
            
            // Initialize food type map if it doesn't exist
            if (!profilesByFood.has(foodType)) {
              profilesByFood.set(foodType, new Map())
            }

            const foodMap = profilesByFood.get(foodType)
            
            if (!foodMap.has(dateHour)) {
              foodMap.set(dateHour, {
                siloWeights: new Map(), // Map to store weights by silo
                profiles: []
              })
            }

            const hourData = foodMap.get(dateHour)
            
            // Initialize array for this silo if it doesn't exist
            if (!hourData.siloWeights.has(profile.silo)) {
              hourData.siloWeights.set(profile.silo, [])
            }
            
            // Add weight to the corresponding silo's array
            if (profile.weight !== null) {  // Solo agregamos valores no nulos al array de weights
              hourData.siloWeights.get(profile.silo).push(profile.weight)
            }
            hourData.profiles.push(profile)
          } catch (error) {
            console.warn('Error processing profile:', error)
          }
        })
      })

      // Convert the nested maps to the final structure
      const result = {}
      profilesByFood.forEach((foodMap, foodType) => {
        // Get all unique hours for this food type
        const allHours = Array.from(foodMap.keys()).sort()
        
        result[foodType] = allHours.map(dateHour => {
          const data = foodMap.get(dateHour)
          let totalLevel = 0
          let siloCount = 0
          
          // Get all silos that have data in any hour
          const allSilos = new Set()
          foodMap.forEach(hourData => {
            hourData.siloWeights.forEach((weights, siloId) => {
              allSilos.add(siloId)
            })
          })

          // Process each silo
          allSilos.forEach(siloId => {
            const weights = data.siloWeights.get(siloId) || []
            
            // Si no hay weights o todos son null, necesitamos interpolar
            if (weights.length === 0 || weights.every(w => w === null)) {
              // Find the closest value for this silo (either before or after this hour)
              let interpolatedValue = null
              const siloData = siloValues.get(siloId)
              
              if (siloData && siloData.length > 0) {
                // Find the closest value before or after this hour
                const closestValue = siloData.reduce((closest, current) => {
                  // Solo consideramos valores no nulos para la interpolación
                  if (current.value === null) return closest
                  if (closest.value === null) return current
                  
                  const currentTime = new Date(current.timestamp)
                  const closestTime = new Date(closest.timestamp)
                  const targetTime = new Date(dateHour)
                  
                  const currentDiff = Math.abs(currentTime - targetTime)
                  const closestDiff = Math.abs(closestTime - targetTime)
                  
                  return currentDiff < closestDiff ? current : closest
                })
                
                if (closestValue && closestValue.value !== null) {
                  interpolatedValue = closestValue.value
                }
              }
              
              if (interpolatedValue !== null) {
                totalLevel += interpolatedValue
                siloCount++
              }
            } else {
              // Si hay weights válidos, calculamos el promedio ignorando los null
              const validWeights = weights.filter(w => w !== null)
              if (validWeights.length > 0) {
                const siloAverage = validWeights.reduce((sum, weight) => sum + weight, 0) / validWeights.length
                totalLevel += siloAverage
                siloCount++
              }
            }
          })
          
          return {
            date: dateHour,
            averageLevel: totalLevel / siloCount,
            totalLevel: totalLevel,
            count: siloCount,
            profiles: data.profiles
          }
        }).sort((a, b) => new Date(a.date) - new Date(b.date))
      })
      return result
    },
  },
}
</script>


